/**
 * RigScreen v2 — a RE-SKIN of the live rig capture states (mockup fidelity).
 *
 * CRITICAL: this re-implements NO capture logic. It reads the EXISTING
 * useCaptureState (RTDB) and calls the EXISTING ingest callables (arm / start /
 * confirm / cancel / reconcile) with the SAME payloads as the live views/rig/*
 * components. Only the presentation is new. The live RigScreen stays the default.
 *
 * Failure/processing states (slow / mint-failed / partial / held) come from a
 * bridge feed that the live app does not yet have (Segment 4). Their VISUALS are
 * ported here and are reviewable via the dev "Preview state" selector; under real
 * data only idle/armed/counting_down/capturing (+ elapsed-derived firing →
 * processing → slow) are reachable. The preview selector is visual-only — its
 * buttons never fire a real write (interactive=false).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, collection, getDocs, doc, getDoc } from "../../../../firebase.js";
import { useCaptureState } from "../../../../lib/useCaptureState.js";
import { usePageIds } from "../../../../lib/feedback/pageIds.js";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { ingest, getDeviceId, newLeaseId } from "../../../../lib/ingestCallables.js";
import { rigv2 } from "./rigv2Callables.js";
import { isStudioWalkins } from "../../../../lib/ingestJobs.js";
import RegistrationQr,{ registrationUrl, openPosterWindow } from "./RegistrationQr.jsx";
import { modalOpen, useModalDialog } from "./dialogFocus.js";
import {
  GroupList,
  againRequest,
  attemptPeople,
  groupCountLabel,
  groupJoinProblem,
  groupJoinProblemText,
  groupOpen,
  groupPeopleOf,
  joinNames,
  maxGroupSizeOf,
  startAllowed,
} from "./GroupCheckin.jsx";

// Rig presets (visual — selecting a rig/plate is local UI, not a write path).
const RIGS = [
  { id: "big", label: "Big Rig", cams: 175, plates: ["Body plate", "Object plate"] },
  { id: "head", label: "Head Rig", cams: 144, plates: ["Head plate"] },
];

const PREVIEW_STATES = [
  ["", "Live (real state)"],
  ["idle", "Idle"],
  ["armed", "Armed"],
  ["counting_down", "Countdown"],
  ["capturing", "Capturing / firing"],
  ["slow", "Slow capture (>240s)"],
  ["mint_failed", "SN mint failed"],
  ["partial", "Partial / flagged"],
  ["held", "Held (transfer stuck)"],
];

const SAMPLE = {
  armed: { phase: "armed", subjectName: "Jordan Vega", subjectDisplayId: "BG_0142", leaseId: null },
  counting_down: { phase: "counting_down", subjectName: "Jordan Vega", expiresAt: null, leaseId: null },
  capturing: { phase: "capturing", subjectName: "Jordan Vega", captureAttemptId: "NYCAP2606080001", leaseId: null },
};

const initials = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "?";

const LIVE_COUNTDOWN_CONFIRM_OFFSET_MS = 5000;
const PREVIEW_COUNTDOWN_MS = 3000;
// How long the Capturing screen keeps saying "Firing / Hold still", counted
// from the moment it sees the capture start. With XCS26 the rig fires about
// 4.2 s after that: the bridge sends the mirror lockup (trigger-warmup, which
// answers in about 0.1 s), waits 2 s, sends the shutter request
// (trigger-managed, at about 2.2 s), and Xangle then counts down 2 s itself
// before it fires. 6 s leaves about 1.8 s for the network, so the guest is
// never told "Pulling files…" before the photo. The bridge writes nothing to
// the Suite between Start and completion, so there is no real "fired" signal
// to show instead. Check against the rehearsal (the bridge window's "shutter
// sent" line and the sound of the rig) and tune here.
const CAPTURE_FIRING_HOLD_MS = 6000;
// A full-size XCS26 capture takes up to about 3 minutes (the 28 Sep live test
// took 53 s for 281 MB; full size is about 1.0 GB, 3.6 times more: ~191 s if
// all of it grows with the bytes): warn only past that, with ~50 s to spare.
// Check against the rehearsal's measured full-size time.
const CAPTURE_SLOW_MS = 240000;
// Mark complete and Abandon appear only once the firing hold is over (they
// used to appear at 3 s, before the rig fires at about 4.2 s: releasing the
// rig then lets a new Start fire while this capture is still being taken),
// and each asks first what Xangle shows.
const ABANDON_CONFIRM = "Abandon this capture? Do this only if Xangle shows no capture for this guest. The SN is burned and a retake gets a new one.";
const MARK_COMPLETE_CONFIRM = "Mark this capture complete? Do this only if Xangle shows this guest's capture finished. Everyone in it is marked captured and the rig takes the next guest.";
const countdownConfirmKeys = new Set();
// The confirm now out for a job and lease (its promise), so a countdown
// screen mounted again while it is out (a tab switch) gets its result.
const countdownConfirmRuns = new Map();

// What the Capturing screen says, from the time since it saw the capture
// start: "firing" (Firing, Hold still) until the rig has surely fired, then
// "processing" (Downloading from cameras, Pulling files…), then "slow".
function capturingStage(elapsedMs) {
  if (elapsedMs >= CAPTURE_SLOW_MS) return "slow";
  if (elapsedMs >= CAPTURE_FIRING_HOLD_MS) return "processing";
  return "firing";
}

// The error code of a failed callable, without the web SDK's "functions/"
// prefix ("functions/failed-precondition" reads "failed-precondition").
function callErrorCode(err) {
  return String(err?.code ?? "").replace(/^functions\//, "");
}

// Codes the server sends only when it refused BEFORE changing anything (a
// busy rig, a stale lease, someone not found, a bad request, no permission).
// Anything else, above all a timeout ("deadline-exceeded"), "unavailable" and
// "internal" (which is also what a dropped connection reads as), leaves it
// unknown whether the call landed.
const CLEAR_REFUSAL_CODES = new Set([
  "failed-precondition", "not-found", "already-exists", "invalid-argument", "permission-denied", "unauthenticated",
]);
function isClearRefusal(err) {
  return CLEAR_REFUSAL_CODES.has(callErrorCode(err));
}

// Group check-in: adding someone already captured makes a retake; asked the
// way the solo Pre-registered path asks (the console and the QR Reader both).
function retakeQuestion(name) {
  return `${String(name ?? "").trim() || "This person"} is already captured. Add them to this capture as a retake?`;
}

// The server refused a group add because the person had been captured and
// the request did not confirm a retake (addGroupMember, details.reason).
function isRetakeRefusal(err) {
  return callErrorCode(err) === "failed-precondition" && err?.details?.reason === "already_captured";
}

// A group add that asks "retake?" when the person had been captured: before
// the call when this screen's roster says so (knownCaptured), else when the
// server refuses because they were (a pass the phone's roster had not
// received, or a roster that was behind), then adds them with retake: true.
// Returns "added" or "declined" (nothing added); throws the add's own error
// otherwise. add(retake) makes the call. Pure (the calls come in), exported
// for the test.
async function addWithRetakeCheck({ add, knownCaptured = false, confirm, name }) {
  if (knownCaptured) {
    if (!confirm(retakeQuestion(name))) return "declined";
    await add(true);
    return "added";
  }
  try {
    await add(false);
    return "added";
  } catch (err) {
    if (!isRetakeRefusal(err)) throw err;
    if (!confirm(retakeQuestion(err?.details?.name || name))) return "declined";
    await add(true);
    return "added";
  }
}

// What a failed group add says. A clear refusal (already in, no consent, the
// capture started) in the server's own words; anything else (a timeout, a
// dropped connection) may have added them, so say that and where to look.
function groupAddErrorText(err, name) {
  if (isClearRefusal(err)) return err?.message ?? String(err);
  const who = String(name ?? "").trim() || "This person";
  return `${who} may have been added: no answer came back. Check the list before adding them again.`;
}

function countdownFireAt(expiresAt, now = Date.now()) {
  if (!expiresAt) return now + PREVIEW_COUNTDOWN_MS;
  return Math.max(now, expiresAt - LIVE_COUNTDOWN_CONFIRM_OFFSET_MS);
}

function countdownConfirmKey(jobId, leaseId) {
  return `${jobId || "no-job"}:${leaseId || "no-lease"}`;
}

function beginCountdownConfirm(key) {
  if (countdownConfirmKeys.has(key)) return false;
  countdownConfirmKeys.add(key);
  return true;
}

function releaseCountdownConfirm(key) {
  countdownConfirmKeys.delete(key);
}

function resetCountdownConfirmGuardsForTests() {
  countdownConfirmKeys.clear();
  countdownConfirmRuns.clear();
}

// onScanToGroup (group check-in): onScanToGroup({ jobId, leaseId }) opens the
// QR Reader ready to add a pass to THAT arm (the reader enters add mode only
// if the rig still shows it); RigV2App passes it, and without it the armed
// screen's add panel offers the roster search only.
export default function RigScreenV2({ jobId, job, sessionId = null, onScanToGroup = null }) {
  const { state: realState, loading, error } = useCaptureState(jobId);
  const [rigId, setRigId] = useState("big");
  const [plate, setPlate] = useState(RIGS[0].plates[0]);
  const [preview, setPreview] = useState(""); // "" = live

  const rig = RIGS.find((r) => r.id === rigId) ?? RIGS[0];
  const interactive = preview === ""; // preview screens never call real writes
  const phase = preview || realState?.phase || "idle";
  const state = preview ? (SAMPLE[preview] ?? realState ?? { phase: "idle" }) : (realState ?? { phase: "idle" });
  // For a feedback report from this screen: the live arm's ids and phase (never a preview's).
  usePageIds("rig-screen", {
    rosterEntryId: realState?.rosterEntryId,
    captureSn: realState?.captureAttemptId,
    mode: realState?.phase,
  });

  return (
    <div className="rigv2-rig">
      <div className="rigv2-modebar">
        <span className="rigv2-modebar-group">
          <span className="rigv2-modebar-label">Rig</span>
          <span className="rigv2-rigpick">
            {RIGS.map((r) => (
              <button key={r.id} type="button" className={r.id === rigId ? "on" : ""}
                onClick={() => { setRigId(r.id); setPlate(r.plates[0]); }}>{r.label}</button>
            ))}
          </span>
          <span className="rigv2-cams">{rig.cams} cameras</span>
        </span>
        <span className="rigv2-modebar-group">
          <span className="rigv2-modebar-label">Plate</span>
          <select className="rigv2-plate" value={plate} onChange={(e) => setPlate(e.target.value)}>
            {rig.plates.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </span>
        {/* Dev/review scaffolding only — the bridge-fed states (mint_failed /
            partial / held) can't be reached without the Segment-4 bridge, so
            emulator builds keep a selector to preview them. Compiled out of prod. */}
        {import.meta.env.VITE_USE_EMULATORS === "true" && (
          <span className="rigv2-preview-pick">
            {!interactive && <span className="rigv2-preview-tag">preview</span>}
            <select value={preview} onChange={(e) => setPreview(e.target.value)} title="Dev-only: preview a redesigned state">
              {PREVIEW_STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </span>
        )}
      </div>

      {error && interactive ? (
        <div className="rigv2-stagebox"><div className="rigv2-banner error">Could not subscribe to capture state: {error.message ?? String(error)}</div></div>
      ) : loading && interactive ? (
        <div className="rigv2-stagebox"><div className="rigv2-sub">Connecting to rig…</div></div>
      ) : (
        <StateScreen phase={phase} job={job} jobId={jobId} sessionId={sessionId} state={state} interactive={interactive} onScanToGroup={onScanToGroup} />
      )}
    </div>
  );
}

function StateScreen({ phase, job, jobId, sessionId, state, interactive, onScanToGroup = null }) {
  switch (phase) {
    case "armed": return <ArmedScreen job={job} state={state} interactive={interactive} onScanToGroup={onScanToGroup} />;
    case "counting_down": return <CountdownScreen job={job} state={state} interactive={interactive} />;
    case "capturing": return <CapturingScreen job={job} state={state} interactive={interactive} />;
    case "slow": return <CapturingScreen job={job} state={state} interactive={interactive} forceElapsed={CAPTURE_SLOW_MS + 1} />;
    case "mint_failed": return <MintFailedScreen state={state} />;
    case "partial": return <PartialScreen state={state} />;
    case "held": return <HeldScreen />;
    default: return <IdleScreen job={job} jobId={jobId} sessionId={sessionId} interactive={interactive} />;
  }
}

// ── Idle: the mode-based capture console (mockup IdleScreen, 3 modes) ───────────
//
// The locked mockup leads the rig HOME with the THREE capture MODES, not the
// old "Waiting for a QR scan" hero (QR scanning is now its own QR Reader
// satellite — the rig reacts to scans done elsewhere). This idle is a faithful
// port of mockups/walkthrough RigScreen.IdleScreen:
//   • Pre-registered — search the roster, pick, arm (the live path; reused verbatim).
//   • Walk-up        — type a name or leave blank, optional Guest/Crew tag, arm.
//   • Register       — full on-the-spot form → a real roster entry → arm.
// Pre-registered is fully wired here (Slice 1). Walk-up (Slice 2) and Register
// (Slice 3) need the additive walk-up callable and are gated until then.
//
// Registration v2 (strategy App. B, S6): the allowWalkUp gate covers ONLY the
// nameless Walk-up mode. Register is the exception path for someone who cannot
// register on their own phone; it asks the same consent and age questions as
// the phone form and stays available when walk-up is off (the server refuses
// only source=walk_up in that case). The roster is a live subscription.

// Optional walk-up tag. The mockup pulls these from ingestSettings.categories,
// but the LIVE IngestSettings has no `categories` field yet (design-gated — see
// docs/ingest/rig-v2-design-brief.md §1), so we fall back to the brief's
// "optional Guest/Crew tag". No invented domain.
const WALKUP_KINDS = ["Guest", "Crew"];

// Which modes can actually arm yet. Flipped on as their backend lands.
const MODE_WIRED = { prereg: true, walkup: true, register: true };

// Register mode form state. ageBand stays null until the operator answers the
// age question ("adult" | "minor"); the guardian fields apply to a minor only.
// consentFor is the wording the tick was given for ({ version, text }: the
// job's registration.consent pair as shown, a few KB at most), set with the
// tick and dropped with it, so a wording change under an open form cannot turn
// a tick given for version 1 into a submission for version 2.
const EMPTY_REG = { name: "", email: "", consent: false, consentFor: null, ageBand: null, guardianName: "", guardianConsent: false };

// One email shape for the station form and the pass email row.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Register mode is complete when the name, a valid email (the server requires
// one for a station registration), the consent tick with the wording it was
// given for, and the age answer are in, plus the guardian's name and tick for
// a minor (strategy App. B). version and text are the wording on screen now:
// a tick given for other wording is not ready, even before the console drops
// it. The Continue button stays off until then.
function registerReady(reg, version, text) {
  if (!reg.name.trim() || !EMAIL_RE.test(reg.email.trim()) || !reg.consent || !reg.consentFor || !reg.ageBand) return false;
  if (consentTickStale(reg, version, text)) return false;
  if (reg.ageBand === "minor") return !!reg.guardianName.trim() && !!reg.guardianConsent;
  return true;
}

// The tick and what it was given for, in one update: ticked binds consentFor to
// the wording on screen (its version and its text); unticked drops it. Bound
// synchronously, with no digest to wait for, so a tick can never land before
// its binding does.
function consentTick(reg, checked, version, text) {
  return { ...reg, consent: checked, consentFor: checked ? { version, text } : null };
}

// True when a tick on file was given for other wording than the one shown now
// (a different version, or a different text). Nothing ticked: false.
function consentTickStale(reg, version, text) {
  const f = reg?.consentFor;
  if (!f) return false;
  return f.version !== version || f.text !== text;
}

// True when the wording on screen (version and text) is not the pair last
// seen. Independent of the ticks, so a guardian-only tick is caught too.
// Nothing seen yet: false, there is nothing to compare against.
function wordingChangedSince(seen, version, text) {
  if (!seen) return false;
  return seen.version !== version || seen.text !== text;
}

// The console's rule for a wording change, pure so it can be checked on its
// own: when the pair on screen is not the pair last seen and either tick is
// set (the guardian's alone counts), both ticks go and consentFor with them.
// Any other case returns reg itself, so the caller can tell nothing happened.
function dropStaleTicks(reg, seen, version, text) {
  if (!wordingChangedSince(seen, version, text) || !(reg.consent || reg.guardianConsent)) return reg;
  return { ...reg, consent: false, guardianConsent: false, consentFor: null };
}

// Register at the station (or a walk-up), then arm: two calls. The new
// registration is never deleted here (group fixes, 2026-09-29: review S4 and
// the astra confirmation round). An arm that timed out, or read as
// "unavailable" or "internal", may still land; and even after a clear refusal
// (the rig was busy) the operator may already be arming the same person by
// another request, which a delete (discardWalkupEntry) could cut from under
// the guest on the rig, taking their consent and contact details with it.
// So after any arm failure the registration stays, and the error carries it
// (err.kept) for the console to pick the person under Pre-registered:
// Continue then arms them by id instead of registering them twice (the
// reason the old cleanup existed, Codex F2). A registration left unused is an
// uncaptured roster entry, which the retention policy removes. deps are the
// two callables, passed in for the test.
async function registerThenArmFlow(payload, { register, arm }) {
  const reg = await register(payload);
  try {
    await arm(reg?.rosterEntryId);
    return reg;
  } catch (armErr) {
    throw keptRegistrationError(armErr, reg);
  }
}

// The error for an arm that failed after a registration that did: the arm's
// own refusal in the server's words (the rig was busy), or, when no clear
// answer came back, that the arm may still land; then what to do.
function keptRegistrationError(cause, reg) {
  const name = String(reg?.name ?? "").trim();
  const who = name && name !== "Unnamed" ? name : `The walk-up${reg?.displayId ? ` No. ${reg.displayId}` : ""}`;
  const text = isClearRefusal(cause) ?
    `${cause?.message ?? String(cause)} ${who} is registered and picked under Pre-registered: press Continue when the rig is free.` :
    `${who} is registered, but the rig did not confirm loading them. If the rig does not show them in a moment, press Continue to load them now.`;
  const err = new Error(text);
  err.kept = { id: reg?.rosterEntryId ?? null, name, displayId: String(reg?.displayId ?? "") };
  err.cause = cause;
  return err;
}

function IdleScreen({ job, jobId, sessionId, interactive }) {
  // Live roster (strategy App. B): a subscription, not a one-shot read, so a
  // person who registers while the rig sits idle appears in the search without
  // a remount. entries is [] until the first snapshot lands (rosterLoading).
  const { data: entries, loading: rosterLoading, error: rosterError } =
    useFirestoreCollection(jobId ? `jobs/${jobId}/rosterEntries` : null);
  const [mode, setMode] = useState("prereg");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null); // a picked roster entry (prereg)
  const [kind, setKind] = useState(null);          // null | "Guest" | "Crew" (walk-up)
  const [reg, setReg] = useState(EMPTY_REG);
  const [arming, setArming] = useState(false);
  const [error, setError] = useState(null);
  const [showRegQr, setShowRegQr] = useState(false);
  const regQrButtonRef = useRef(null);  // where focus goes back when the overlay closes
  const [browseOpen, setBrowseOpen] = useState(false);
  const [recent, setRecent] = useState([]);   // last few captureAttempts
  const defaultedRef = useRef(false);

  // Recent captures strip — live captureAttempts, loaded on each idle return
  // (StateScreen remounts IdleScreen when the rig comes back to idle, so this
  // refreshes after every capture). Faithful to the mockup's RecentCapturesStrip.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, "jobs", jobId, "captureAttempts"));
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (b.firedAt ?? b.triggeredAt ?? 0) - (a.firedAt ?? a.triggeredAt ?? 0))
          .slice(0, 4);
        setRecent(rows);
      } catch { /* recent strip is best-effort; never blocks the console */ }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const remaining = entries.filter((r) => !r.captured);

  // Locked (item 2): walk-up is ON BY DEFAULT, allowed unless the job sets
  // allowWalkUp explicitly false (matches the IngestSettingsSchema default and
  // the server gate). The gate covers ONLY the nameless Walk-up mode (S6 gate
  // split): Pre-registered and Register always work.
  // The studio walk-ins job (Quick guest) never has it: everyone on it
  // registers with consent, whatever the setting says (the server refuses it too).
  const walkUpAllowed = job?.ingestSettings?.allowWalkUp !== false && !isStudioWalkins(job);

  // The consent statement this screen shows: its version and its text. A tick
  // in Register mode is bound to both (reg.consentFor). A wording change is
  // caught apart from the ticks: the pair last seen lives in a ref, and when
  // the live pair moves while either tick is set (the guardian's alone counts)
  // both ticks are dropped and the operator is asked to read the new wording
  // and tick again. The ref is brought up to date on every run, so one change
  // is acted on once.
  const consentVersion = String(job?.registration?.consent?.version ?? "1");
  const consentText = String(job?.registration?.consent?.text ?? "");
  const seenWordingRef = useRef(null);
  const [wordingNote, setWordingNote] = useState(false);
  useEffect(() => {
    const seen = seenWordingRef.current;
    seenWordingRef.current = { version: consentVersion, text: consentText };
    if (dropStaleTicks(reg, seen, consentVersion, consentText) === reg) return;
    setReg((r) => dropStaleTicks(r, seen, consentVersion, consentText));
    setWordingNote(true);
  }, [consentVersion, consentText, reg]);
  // The changed-wording sentence clears once the box is ticked again.
  useEffect(() => { if (reg.consent) setWordingNote(false); }, [reg.consent]);

  // kind (guest/crew) label lookup by rosterEntryId, for the recent-captures
  // strip (the captureAttempt itself doesn't carry the label).
  const kindById = useMemo(() => {
    const m = {};
    for (const r of entries) { const k = r.customFields?.kind; if (k) m[r.id] = k; }
    return m;
  }, [entries]);

  // Default mode once the roster loads: Pre-registered if anyone is waiting,
  // else Walk-up when the job allows it, else Register (mirrors the mockup).
  useEffect(() => {
    if (rosterLoading || defaultedRef.current) return;
    defaultedRef.current = true;
    if (remaining.length === 0) setMode(walkUpAllowed ? "walkup" : "register");
  }, [rosterLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // If walk-up gets turned off (or the job loads with it off), never leave the
  // operator stranded on the now-hidden Walk-up mode. Register stays available.
  useEffect(() => {
    if (!walkUpAllowed && mode === "walkup") switchMode("prereg");
  }, [walkUpAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = query.trim().toLowerCase();
  const matches = (mode === "prereg" && q && !selected)
    ? entries.filter((p) => {
        const pf = p.personFields || {}, cf = p.customFields || {};
        return [p.displayId, p.name, p.subjectType, pf.phone, pf.email, cf.union, cf.role, cf.scene, cf.edbrStatus]
          .filter(Boolean).map((v) => String(v).toLowerCase()).join(" ").includes(q);
      }).slice(0, 5)
    : [];

  const switchMode = (m) => {
    setMode(m); setQuery(""); setSelected(null); setKind(null); setError(null);
    setReg(EMPTY_REG); setWordingNote(false);
  };

  // Pre-registered arm — EXACT live wiring (RigIdle.jsx); reused, not re-implemented.
  const armByRosterEntryId = async (rosterEntryId) =>
    ingest.arm({
      jobId: job.id, sessionId: sessionId ?? null, rosterEntryId,
      deviceId: getDeviceId(), clientLeaseId: newLeaseId(),
    });

  const handleContinue = async () => {
    if (arming || !interactive) return;
    if (!MODE_WIRED[mode]) return;
    if (mode === "prereg" && !selected) return;
    if (mode === "register" && !registerReady(reg, consentVersion, consentText)) return;
    setArming(true); setError(null);
    try {
      if (mode === "prereg") {
        if (selected.captured && !window.confirm(`${selected.name || selected.displayId} is already captured. Create a retake?`)) {
          setArming(false); return;
        }
        await armByRosterEntryId(selected.id);
      } else if (mode === "walkup") {
        // DESIGN-Q(ivin): the mockup arms a BLANK walk-up with rosterEntryId=null
        // (a name-later synthetic subject). The live armParticipant REQUIRES a
        // rosterEntry and is guardrail-locked, so we take option (A) from the
        // design brief: mint a lightweight walk_up rosterEntry ("Unnamed" when
        // blank) then reuse the live arm-by-id path verbatim — one arm path.
        await registerThenArm({ name: query.trim() || null, kind: kind || null });
      } else if (mode === "register") {
        // Register at the station: the exception path for someone who cannot
        // register on their own phone. A FULL roster entry with the same consent
        // record as the phone form (consentVersion is the version the tick was
        // given for, reg.consentFor, not the live one, so the server binds the
        // acceptance to the wording the person read and refuses a stale tick),
        // then arm by id. source=self_registration keeps the path open when
        // Walk-up is off. Blank optional fields stay undefined so the wrapper
        // omits them; the email is required, registerReady checks it.
        const minor = reg.ageBand === "minor";
        await registerThenArm({
          name: reg.name.trim(),
          email: reg.email.trim(),
          consent: true,
          consentVersion: reg.consentFor.version,
          ageBand: reg.ageBand,
          guardianName: minor ? reg.guardianName.trim() : undefined,
          guardianConsent: minor ? true : undefined,
          source: "self_registration",
        });
      }
    } catch (err) {
      if (err?.kept?.id) {
        // The registration stays (registerThenArmFlow never deletes it). If
        // the arm landed after all, the rig screen moves on by itself; if not,
        // the person is picked under Pre-registered, and Continue loads them
        // without a second registration.
        const kept = err.kept;
        setMode("prereg"); setQuery(""); setKind(null); setReg(EMPTY_REG); setWordingNote(false);
        setSelected(entries.find((r) => r.id === kept.id) ?? { id: kept.id, name: kept.name, displayId: kept.displayId, captured: false });
      }
      setError(err); setArming(false);
    }
  };

  // Register, then arm (registerThenArmFlow). An arm that fails leaves the
  // registration in place: the console moves to Pre-registered with that
  // person picked, so Continue loads them by id instead of registering them
  // a second time.
  const registerThenArm = (payload) => registerThenArmFlow(payload, {
    register: (p) => rigv2.registerWalkup({ jobId: job.id, ...p }),
    arm: (rosterEntryId) => armByRosterEntryId(rosterEntryId),
  });

  const canContinue = interactive && !arming && MODE_WIRED[mode] && (
    mode === "walkup" || (mode === "prereg" && !!selected) || (mode === "register" && registerReady(reg, consentVersion, consentText))
  );

  // A failed subscription is terminal (onSnapshot stops after an error), so the
  // console shows the error instead of a silently frozen roster.
  if (rosterError) {
    return <div className="rigv2-stagebox"><div className="rigv2-banner error">{rosterError.message ?? String(rosterError)}</div></div>;
  }

  return (
    <div className="rigv2-stagebox rigv2-idle">
      <div className="rigv2-corner-tag"><span className="rigv2-corner-dot" />{job?.displayName ?? job?.clientName ?? job?.id}</div>

      <p className="rigv2-tocapture">
        <span>{entries.length - remaining.length} of {entries.length} captured · {remaining.length} waiting</span>
        {!rosterLoading && <span className="rigv2-live" title="The roster is a live subscription"><i />Live</span>}
      </p>

      {/* Capture console — pick a pre-registered subject, a walk-up, or register. */}
      <div className="rigv2-console">
        <div className="rigv2-console-title">Next capture</div>

        <div className="rigv2-modeseg" role="group" aria-label="Capture mode">
          <button type="button" className={mode === "prereg" ? "on" : ""} aria-pressed={mode === "prereg"} onClick={() => switchMode("prereg")}>Pre-registered</button>
          {walkUpAllowed && <button type="button" className={mode === "walkup" ? "on" : ""} aria-pressed={mode === "walkup"} onClick={() => switchMode("walkup")}>Walk-up</button>}
          <button type="button" className={mode === "register" ? "on" : ""} aria-pressed={mode === "register"} onClick={() => switchMode("register")}>Register</button>
        </div>

        {mode === "prereg" ? (
          selected ? (
            <div className="rigv2-selected">
              <span className="rigv2-roster-av">{initials(selected.name)}</span>
              <span className="rigv2-selected-body">
                <span className="rigv2-roster-name">{selected.name || selected.displayId || "—"}</span>
                <span className="rigv2-roster-meta">from roster{[selected.customFields?.role, selected.customFields?.scene].filter(Boolean).map((s) => ` · ${s}`).join("")}</span>
              </span>
              <RosterBadges entry={selected} className="rigv2-roster-badges" />
              <button type="button" className="rigv2-selected-clear" aria-label="Clear" onClick={() => setSelected(null)}>×</button>
            </div>
          ) : (
            <div>
              <input type="text" className="rigv2-field" value={query} autoFocus
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && matches.length) { e.preventDefault(); setSelected(matches[0]); setQuery(""); } }}
                placeholder="Search name or number" />
              {matches.length > 0 && (
                <div className="rigv2-matchlist">
                  {matches.map((p) => (
                    <RosterRow key={p.id} entry={p} onPick={() => { setSelected(p); setQuery(""); }} />
                  ))}
                </div>
              )}
              {!q && <p className="rigv2-console-hint">{rosterLoading ? "Loading roster…" : `${remaining.length} waiting · the QR Reader can also load them here.`}{!walkUpAllowed ? " Walk-up is off for this job." : ""}</p>}
            </div>
          )
        ) : mode === "register" ? (
          <RegisterFields reg={reg} setReg={setReg} consentVersion={consentVersion} consentText={consentText} wordingChanged={wordingNote} />
        ) : (
          <>
            <input type="text" className="rigv2-field" value={query} autoFocus
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type a name — or leave blank" />
            <div className="rigv2-kindrow">
              <span className="rigv2-roster-meta">Mark as</span>
              {WALKUP_KINDS.map((c) => (
                <button key={c} type="button" className={`rigv2-kindchip ${kind === c ? "on" : ""}`} onClick={() => setKind(kind === c ? null : c)}>{c}</button>
              ))}
            </div>
          </>
        )}

        {error && <div className="rigv2-banner error">{error.message ?? String(error)}</div>}

        <button type="button" className="rigv2-btn primary rigv2-fire" onClick={handleContinue} disabled={!canContinue}>
          {arming ? "Loading rig…" : "Continue to capture"}
        </button>
        {(() => {
          const hint = !MODE_WIRED[mode]
            ? (mode === "walkup" ? "Walk-up arming lands in the next slice." : "On-the-spot registration lands in a later slice.")
            : mode === "prereg" ? (selected ? null : "Pick a registered subject to continue.")
            : mode === "register" ? "Creates a full roster entry with a number, then loads them to capture. Continue stays off until the consent and age answers are made."
            : "Blank is fine — name it later from the contact sheet.";
          return hint ? <p className="rigv2-console-hint center">{hint}</p> : null;
        })()}
      </div>

      {/* Browse the full roster + the job's self-registration QR (secondary). */}
      <div className="rigv2-idle-foot">
        {entries.length > 0 ? (
          <button type="button" className="rigv2-linkbtn" onClick={() => setBrowseOpen((v) => !v)}>
            {browseOpen ? "▾" : "▸"} Browse the roster ({remaining.length} waiting)
          </button>
        ) : <span />}
        <button type="button" className="rigv2-linkbtn" ref={regQrButtonRef} onClick={() => setShowRegQr(true)}>⧉ Registration QR</button>
      </div>
      {browseOpen && entries.length > 0 && (
        <div className="rigv2-browse">
          {entries.map((p) => (
            <RosterRow key={p.id} entry={p} capturedTag disabled={!interactive || arming}
              onPick={() => { switchMode("prereg"); setSelected(p); setBrowseOpen(false); }} />
          ))}
        </div>
      )}
      {showRegQr && <RegQrOverlay job={job} returnFocusRef={regQrButtonRef} onClose={() => setShowRegQr(false)} />}

      <RecentCapturesStrip recent={recent} interactive={interactive} arming={arming} kindById={kindById}
        onAgain={async (cap) => {
          const again = againRequest(cap);
          if (!again || arming || !interactive) return;
          setArming(true); setError(null);
          try {
            // Group check-in: a group's retake is the whole group, armed in
            // one step (armGroup: everyone or nobody, so Start can never catch
            // it half rebuilt). One person: the live arm path, as before.
            if (again.kind === "group") {
              await rigv2.armGroup({
                jobId: job.id, sessionId: sessionId ?? null, deviceId: getDeviceId(), clientLeaseId: newLeaseId(),
                rosterEntryIds: again.rosterEntryIds,
              });
            } else {
              await armByRosterEntryId(again.rosterEntryId);
            }
          }
          catch (err) { setError(err); setArming(false); }
        }} />
    </div>
  );
}

// Recent captures strip — live captureAttempts (mockup RecentCapturesStrip).
// "Capture again" reuses the live arm-by-id path; disabled for walk-up captures
// that carried no rosterEntryId.
function captureAttemptName(c) {
  return (c.subjectSnapshot?.displayNameAtFire || c.subjectSnapshot?.subjectName || c.displayName || c.subjectName || "").trim();
}
function captureAttemptStatus(c) {
  const s = c.state ?? c.status;
  if (s === "complete") return "complete";
  if (s === "abandoned" || s === "failed") return "flagged";
  return "progress";
}
function RecentCapturesStrip({ recent, onAgain, interactive, arming, kindById = {} }) {
  const cards = recent ?? [];
  return (
    <div className="rigv2-recent">
      <div className="rigv2-recent-title"><span>Recent captures</span><span className="rigv2-roster-meta">Last {cards.length}</span></div>
      {cards.length === 0 ? (
        <div className="rigv2-recent-empty">No captures yet on this job.</div>
      ) : (
        <div className="rigv2-recent-cards">
          {cards.map((c) => {
            const name = captureAttemptName(c);
            const rosterEntryId = c.subjectSnapshot?.rosterEntryId;
            const kind = rosterEntryId ? kindById[rosterEntryId] : null;
            const groupSize = attemptPeople(c).length; // group check-in: 2+ for a group
            return (
              <div key={c.id} className="rigv2-recent-card">
                <span className={`rigv2-tile-status ${captureAttemptStatus(c)} rigv2-recent-dot`} title={c.state ?? c.status ?? ""} />
                <div className="rigv2-recent-head">
                  <span className="rigv2-roster-av">{name ? initials(name) : "·"}</span>
                  <div className="rigv2-recent-body">
                    <span className={`rigv2-recent-name ${name ? "" : "unnamed"}`}>
                      {name || "Walk-up · unnamed"}
                      {groupSize > 1 && <span className="rigv2-recent-group" title={groupCountLabel(groupSize)}>+{groupSize - 1}</span>}
                    </span>
                    {kind && <span className="rigv2-kind-tag">{kind}</span>}
                  </div>
                </div>
                <span className="rigv2-tile-sn mono" title={c.id}>{c.id}</span>
                <button type="button" className="rigv2-recent-again" disabled={!rosterEntryId || !interactive || arming}
                  title={rosterEntryId ? (groupSize > 1 ? "Capture this group again" : "Capture this subject again") : "Walk-up — re-arm from the console"}
                  onClick={() => onAgain(c)}>{groupSize > 1 ? "Capture group again" : "Capture again"}</button>
              </div>
            );
          })}
          {Array.from({ length: Math.max(0, 4 - cards.length) }).map((_, i) => (
            <div key={`empty-${i}`} className="rigv2-recent-card empty">—</div>
          ))}
        </div>
      )}
    </div>
  );
}

// Consent and minor badges (mockup frames 10 and 12), read off a roster entry.
// Registration v2 entries carry personFields.consent = {accepted, version, ...};
// entries from before it carry only customFields.consent: "yes" (no version).
// A minor carries personFields.isMinor and personFields.guardianName.
function consentBadge(entry) {
  const consent = entry?.personFields?.consent;
  if (consent?.accepted) return consent.version ? `consent v${consent.version}` : "consent";
  if (entry?.customFields?.consent === "yes") return "consent";
  return null;
}
function minorBadge(entry) {
  const pf = entry?.personFields;
  if (!pf?.isMinor) return null;
  return `minor, guardian: ${pf.guardianName || "not recorded"}`;
}
function RosterBadges({ entry, className = "rigv2-badges" }) {
  const consent = consentBadge(entry);
  const minor = minorBadge(entry);
  if (!consent && !minor) return null;
  return (
    <span className={className}>
      {consent && <span className="rigv2-bdg ok">{consent}</span>}
      {minor && <span className="rigv2-bdg mn">{minor}</span>}
    </span>
  );
}

// One roster row, shared by the search results and the browse list: the name
// with the number next to it, a meta line (email first, since it breaks ties
// between two people with the same name), and the badges. capturedTag puts the
// "Captured" tag at the right (browse list) instead of inline after the name.
function RosterRow({ entry: p, onPick, disabled = false, capturedTag = false }) {
  // The address is masked here too: the station monitor faces the room.
  const meta = [maskEmail(p.personFields?.email), p.subjectType, p.customFields?.role, p.customFields?.union].filter(Boolean).join(" · ");
  return (
    <button type="button" className="rigv2-roster-row" disabled={disabled} onClick={onPick}>
      <span className="rigv2-roster-av">{initials(p.name)}</span>
      <span className="rigv2-roster-main">
        <span className="rigv2-roster-name">
          {p.name || p.displayId || "Unnamed"}
          {p.name && p.displayId ? <span className="rigv2-roster-num">{p.displayId}</span> : null}
          {p.captured && !capturedTag && <span className="rigv2-roster-meta"> · captured</span>}
        </span>
        {meta && <span className="rigv2-roster-meta" style={{ display: "block" }}>{meta}</span>}
      </span>
      <span className="rigv2-roster-side">
        <RosterBadges entry={p} className="rigv2-roster-badges" />
        {p.captured && capturedTag && <span className="rigv2-roster-cap">Captured</span>}
      </span>
    </button>
  );
}

// Register at the station (mockup frame 11): the same questions and the same
// consent wording as the phone form. consentVersion is the job's current
// registration.consent.version, shown so the operator reads the right statement;
// consentText is that wording itself, and the tick records both, so the
// submit is bound to the wording the person read. wordingChanged shows the
// changed-wording sentence after the console dropped a stale tick.
// The guardian block appears only for the under-18 answer.
// The consent wording is shown here in full: the server records the text the
// job holds as what this person accepted, so the station must display it, the
// way the phone form does. With no wording on the job the server refuses a
// station registration; say so instead of collecting a tick against nothing.
const NO_CONSENT_TEXT_HINT = "This job has no consent statement yet. Add it on the Settings tab before registering anyone here.";
const EMAIL_HINT = "Enter a full email address, like name@example.com.";
const WORDING_CHANGED_HINT = "The consent statement changed. Read the new wording and tick again.";

function RegisterFields({ reg, setReg, consentVersion = "1", consentText = "", wordingChanged = false }) {
  const upd = (k, v) => setReg((r) => ({ ...r, [k]: v }));
  const minor = reg.ageBand === "minor";
  const wording = String(consentText ?? "").trim();
  const email = reg.email.trim();
  const emailBad = !!email && !EMAIL_RE.test(email);
  return (
    <div className="rigv2-reg-fields">
      <p className="rigv2-console-hint rigv2-reg-intro">For people who cannot register on their own phone. Same questions and the same consent wording as the form.</p>
      <input className="rigv2-field" placeholder="Full name *" value={reg.name} onChange={(e) => upd("name", e.target.value)} autoFocus />
      <input className="rigv2-field" type="email" placeholder="Email *" value={reg.email} onChange={(e) => upd("email", e.target.value)} />
      {emailBad && <span className="rigv2-console-hint" style={{ margin: 0 }}>{EMAIL_HINT}</span>}
      {wording ? (
        <div className="rigv2-reg-consent-text" aria-label={`Consent statement, version ${consentVersion}`}>{wording}</div>
      ) : (
        <div className="rigv2-banner error">{NO_CONSENT_TEXT_HINT}</div>
      )}
      <label className="rigv2-reg-consent">
        <input type="checkbox" checked={reg.consent} disabled={!wording}
          onChange={(e) => setReg((r) => consentTick(r, e.target.checked, consentVersion, consentText))} />
        <span>Agrees to the consent statement above (version {consentVersion}). Read it to them or show them this screen.</span>
      </label>
      {wordingChanged && <span className="rigv2-console-hint" role="alert" style={{ margin: 0 }}>{WORDING_CHANGED_HINT}</span>}
      <div className="rigv2-reg-age" role="radiogroup" aria-label="Age">
        <label className="rigv2-reg-consent">
          <input type="radio" name="rigv2-ageband" checked={reg.ageBand === "adult"} onChange={() => upd("ageBand", "adult")} />
          <span>18 or older</span>
        </label>
        <label className="rigv2-reg-consent">
          <input type="radio" name="rigv2-ageband" checked={minor} onChange={() => upd("ageBand", "minor")} />
          <span>Under 18, guardian present</span>
        </label>
      </div>
      {minor && (
        <div className="rigv2-minor">
          <span className="rigv2-console-hint" style={{ margin: 0 }}>A parent or guardian must be with them at the station.</span>
          <input className="rigv2-field" placeholder="Parent or guardian's full name *" value={reg.guardianName} onChange={(e) => upd("guardianName", e.target.value)} />
          <label className="rigv2-reg-consent">
            <input type="checkbox" checked={reg.guardianConsent} onChange={(e) => upd("guardianConsent", e.target.checked)} />
            <span>The guardian agrees to the consent statement on their behalf.</span>
          </label>
        </div>
      )}
    </div>
  );
}

// Registration QR overlay (mockup frame 10): the same code as the poster and
// the QR board. The token is minted on the Settings tab and lives on
// job.registration.publicToken; without one there is nothing to show yet.
function RegQrOverlay({ job, onClose, returnFocusRef = null }) {
  const registration = job?.registration || {};
  const url = registration.publicToken ? registrationUrl(registration.publicToken) : null;
  const eventTitle = registration.eventTitle || job?.displayName || job?.id;
  // A modal dialog (useModalDialog): focus goes to Close when it opens, the
  // console and the Suite around it are inert, Tab and Shift+Tab stay inside
  // it, and on closing focus goes back to the Registration QR button
  // (returnFocusRef: Safari does not focus a tapped button, so "whatever had
  // focus before" can be the wrong element), or, when the idle screen went
  // away under it (the rig armed from another device), to the rig's current
  // tab. Escape closes it; onClose is read through a ref, so a re-render of
  // the console behind it does not add a second listener.
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  useModalDialog(dialogRef, { initialFocusRef: closeButtonRef, returnFocusRef });
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  return (
    <div className="rigv2-modal-scrim" onClick={onClose}>
      <div
        className="rigv2-modal" role="dialog" aria-modal="true" aria-labelledby="rigv2-regqr-title" tabIndex={-1} ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="rigv2-modal-title" id="rigv2-regqr-title">Scan to register</div>
        <p className="rigv2-sub rigv2-regqr-title">{eventTitle}</p>
        {url ? (
          <div className="rigv2-regqr-wrap"><RegistrationQr url={url} size={220} /></div>
        ) : (
          <p className="rigv2-regqr-none">No registration code yet. Create one on the Settings tab.</p>
        )}
        <p className="rigv2-console-hint center">People register on their own phone. Each gets their own pass.</p>
        <div className="rigv2-actions rigv2-regqr-actions">
          <button type="button" className="rigv2-btn" disabled={!url}
            onClick={() => openPosterWindow({ url, eventTitle: registration.eventTitle, hostLine: registration.hostLine })}>Print poster</button>
          <button type="button" className="rigv2-btn primary" ref={closeButtonRef} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Pass email (M1 + M4): fix an address and resend the pass at the station ───
// The public pass page no longer takes a new email (a photographed pass could
// redirect someone's delivery), so the correction lives here, behind crew auth.
// The two callables need only the ids; the roster entry supplies the address
// shown, masked the way the pass page masks it.

// "maya.okafor@example.com" reads as "m***@example.com"; no address, no mask.
function maskEmail(email) {
  const s = String(email ?? "").trim();
  const at = s.indexOf("@");
  return at < 1 ? "" : `${Array.from(s)[0]}***@${s.slice(at + 1)}`;
}

// The line after a fix (saved=true) or a resend, from the server's answer:
// fixRosterEmail returns { ok, emailMasked, emailed, status } where status is
// the email status record { status, at, attempts, messageId? }; resend returns
// that record itself. The word is read from either shape first; without one,
// emailed stands in. A fix on a job frozen after its event (the arm was on
// the rig before the freeze) says passNotSent "capture_frozen": the address
// was saved for the guest's delivery and no pass was sent.
function passEmailResultText(res, masked, saved = false) {
  const addr = res?.emailMasked || masked || "their email";
  if (res?.passNotSent === "capture_frozen") return `Address saved: ${addr}. No pass was sent because captures on this job are closed.`;
  const s = typeof res?.status === "object" && res.status ? res.status.status : res?.status;
  const status = s || (res?.emailed ? "sent" : "failed");
  if (status === "sent") return `Pass sent to ${addr}.`;
  if (status === "pending") return `${saved ? "Saved. " : ""}Sending the pass to ${addr}.`;
  return `${saved ? "Saved, email" : "Email"} failed, the pass on their phone still works.`;
}

// A late email save merges into the loaded entry only when it is the entry the
// save was for, on the job it was for; a null entry (still loading), another
// person's, or another job's stays as is.
function mergeSavedEmail(entry, email, forEntryId, forJobId, currentJobId) {
  if (!entry || entry.id !== forEntryId || forJobId !== currentJobId) return entry;
  return { ...entry, personFields: { ...(entry.personFields || {}), email } };
}

// Keyboard shortcuts on the armed screen fire only from the body: a keypress
// with nothing focused. Any other target keeps the key to itself, focusable or
// not (a focused button already turns Enter and Space into its own click, so
// the window handler must not fire the rig on top of it: Tab to "Save and
// resend", Enter, the address is saved and the rig stays put). A missing
// target counts as the body. doc is a seam for tests, which have no document.
function isTypingTarget(el, doc = globalThis.document) {
  if (el == null) return false;
  return el !== doc?.body && el !== doc?.documentElement;
}


// Whether a keydown on the armed screen starts the capture. Pure: the screen
// passes what it knows (modalOpen: dialogFocus.js says a modal dialog such as
// the QR Reader is open, where a button that goes away, like Send to rig or Add
// a person to this capture, drops focus to the body); exported for the test.
function armedShortcutFires({ key, repeat = false, typingTarget = false, modalOpen = false, addOpen = false, busy = null, groupBusy = null, interactive = true }) {
  if (key !== " " && key !== "Enter") return false;
  if (repeat || typingTarget || modalOpen || addOpen) return false;
  return startAllowed({ busy, groupBusy, interactive });
}

// email: the address on file (undefined when the entry is still loading, then
// emailKnown=false and Resend is left to the server). interactive=false is the
// preview selector: everything disabled, nothing fires. A call that settles
// after the row unmounted (the rig moved on) is dropped: no state update and
// no callback into a parent that has moved on too.
function PassEmailRow({ jobId, rosterEntryId, email, emailKnown = true, interactive = true, onEmailSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null); // "save" | "resend" | null
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const masked = maskEmail(email);
  const canAct = interactive && !busy && !!jobId && !!rosterEntryId;
  const draftValid = EMAIL_RE.test(draft.trim());

  const run = async (kind, fn, onOk) => {
    if (!canAct) return;
    setBusy(kind); setError(null); setNote(null);
    try {
      const res = await fn();
      if (mountedRef.current) onOk(res);
    } catch (err) {
      if (mountedRef.current) setError(err);
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };
  const save = () => {
    const next = draft.trim();
    if (!EMAIL_RE.test(next)) return;
    run("save", () => rigv2.fixRosterEmail({ jobId, rosterEntryId, email: next }), (res) => {
      setEditing(false); setDraft("");
      setNote(passEmailResultText(res, maskEmail(next), true));
      if (onEmailSaved) onEmailSaved(next, rosterEntryId, jobId);
    });
  };
  const resend = () => run("resend", () => rigv2.resendRegistrationPass({ jobId, rosterEntryId }),
    (res) => setNote(passEmailResultText(res, masked)));
  const startEdit = () => { setDraft(""); setNote(null); setError(null); setEditing(true); };
  const stopEdit = () => { setEditing(false); setError(null); };

  return (
    <div className="rigv2-passmail">
      <div className="rigv2-passmail-row">
        <span className="rigv2-roster-meta">Pass email</span>
        {editing ? (
          <form className="rigv2-passmail-form" onSubmit={(e) => { e.preventDefault(); save(); }}>
            <input className="rigv2-set-txt" type="email" inputMode="email" autoComplete="off" placeholder="name@example.com"
              aria-label="New email" value={draft} onChange={(e) => setDraft(e.target.value)} disabled={!!busy} autoFocus />
            <button type="submit" className="rigv2-set-sbtn" disabled={!canAct || !draftValid}>{busy === "save" ? "Saving…" : "Save and resend"}</button>
            <button type="button" className="rigv2-set-sbtn" disabled={!!busy} onClick={stopEdit}>Cancel</button>
          </form>
        ) : (
          <>
            {emailKnown && <span className="mono rigv2-passmail-addr">{masked || "No email on file"}</span>}
            <button type="button" className="rigv2-set-sbtn" disabled={!canAct} onClick={startEdit}>Edit</button>
            <button type="button" className="rigv2-set-sbtn" disabled={!canAct || (emailKnown && !masked)} onClick={resend}>
              {busy === "resend" ? "Sending…" : "Resend pass"}
            </button>
          </>
        )}
      </div>
      {note && <p className="rigv2-console-hint center rigv2-passmail-note">{note}</p>}
      {error && <div className="rigv2-banner error">{error.message ?? String(error)}</div>}
    </div>
  );
}

// ── Armed (start / cancel — exact live wiring from RigArmed.jsx) ───────────────
function ArmedScreen({ job, state, interactive, onScanToGroup = null }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  // Group check-in: the add panel is open (Space/Enter must not fire the rig
  // while the operator is picking people), and groupBusy is the roster id
  // being added or taken out right now: Start waits for it, so a Start
  // pressed a moment after "add" never fires without the person on the way.
  const [addOpen, setAddOpen] = useState(false);
  const [groupBusy, setGroupBusy] = useState(null);
  // Mockup: armed is GREEN for pre-registered, AMBER for walk-ups, and shows
  // the consent and minor badges (frame 12). The capture state carries none of
  // that, so read the armed roster entry; until the read lands (or if it fails)
  // we stay green with no badges.
  const [entry, setEntry] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setEntry(null);
    if (!state.rosterEntryId || !job?.id) return undefined;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "jobs", job.id, "rosterEntries", state.rosterEntryId));
        if (!cancelled && snap.exists()) setEntry({ id: snap.id, ...snap.data() });
      } catch { /* default green, no badges */ }
    })();
    return () => { cancelled = true; };
  }, [job?.id, state.rosterEntryId]);
  const walkUp = entry?.source === "walk_up";
  const guardianName = entry?.personFields?.isMinor ? (entry.personFields.guardianName || "the guardian") : null;
  // Pass email row (M1 + M4). A saved address is written back into the local
  // entry so the masked line reads the new one at once.
  const rosterEntryId = state.rosterEntryId || null;
  // forEntryId and forJobId guard a save that resolves after the rig moved to
  // someone else, or to another job: it lands only on the loaded entry it was
  // for (the row's note already says where the pass went), never on the next
  // person's line and never as a made-up entry while the real one is still
  // loading. The job to compare against is read from a ref at merge time; the
  // closure the callback was made in would hand back the same stale id the
  // row passes in.
  const jobIdRef = useRef(job?.id);
  useEffect(() => { jobIdRef.current = job?.id; }, [job?.id]);
  const onEmailSaved = (email, forEntryId, forJobId) => setEntry((e) => mergeSavedEmail(e, email, forEntryId, forJobId, jobIdRef.current));

  const onStart = async () => {
    if (!startAllowed({ busy, groupBusy, interactive })) return;
    setBusy("start"); setError(null);
    try { await ingest.start({ jobId: job.id, leaseId: state.leaseId, deviceId: getDeviceId() }); }
    catch (err) { setError(err); setBusy(null); }
  };
  const onCancel = async () => {
    if (!interactive || busy) return;
    setBusy("cancel"); setError(null);
    try { await ingest.cancel({ jobId: job.id, leaseId: state.leaseId, deviceId: getDeviceId() }); }
    catch (err) { setError(err); setBusy(null); }
  };
  useEffect(() => {
    const onKey = (e) => {
      // Typing in the pass email field: Enter submits that form, never the rig.
      // A dialog over the console (the QR Reader) owns the keyboard, wherever
      // focus is: Space or Enter must not start a capture behind it (modalOpen).
      // Group check-in: not while the add panel is open or a group change from
      // this screen is still out (armedShortcutFires, startAllowed).
      if (!armedShortcutFires({
        key: e.key, repeat: e.repeat, typingTarget: isTypingTarget(e.target), modalOpen: modalOpen(),
        addOpen, busy, groupBusy, interactive,
      })) return;
      e.preventDefault(); onStart();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, interactive, addOpen, groupBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rigv2-stagebox">
      <button type="button" className="rigv2-btn ghost rigv2-corner-cancel" disabled={!!busy || !interactive} onClick={onCancel}>
        {busy === "cancel" ? "Cancelling…" : "Cancel"}
      </button>
      <div className={`rigv2-eyebrow ${walkUp ? "capture" : "green"}`}><span className="rigv2-dot" />Armed · ready to capture</div>
      <div className="rigv2-avatar">{initials(state.subjectName)}</div>
      <div className="rigv2-subject-name">{state.subjectName || "—"}</div>
      <div className="rigv2-subject-id">{state.subjectDisplayId || "—"}</div>
      <RosterBadges entry={entry} />
      {guardianName && <p className="rigv2-console-hint center rigv2-armed-note">Check that {guardianName} is at the station before Start.</p>}
      <ArmedGroup job={job} state={state} interactive={interactive} addOpen={addOpen} setAddOpen={setAddOpen}
        busyId={groupBusy} setBusyId={setGroupBusy} onScanToGroup={onScanToGroup} />
      {error && <div className="rigv2-banner error">{error.message ?? String(error)}</div>}
      <div className="rigv2-actions">
        <button type="button" className="rigv2-btn primary" disabled={!startAllowed({ busy, groupBusy, interactive })} onClick={onStart}>
          {busy === "start" ? "Starting…" : "Start capture"}
        </button>
      </div>
      {rosterEntryId && job?.id ? (
        <PassEmailRow key={`${job.id}:${rosterEntryId}`} jobId={job.id} rosterEntryId={rosterEntryId} email={entry?.personFields?.email}
          emailKnown={entry !== null} interactive={interactive} onEmailSaved={onEmailSaved} />
      ) : null}
    </div>
  );
}

// ── Group check-in on the armed screen ───────────────────────────────────────
// The group on the rig (the first person, then everyone added), a remove
// button per added person, and "Add person": search the roster (everyone
// registered, walk-ups registered at the station included) or scan a pass
// with the QR Reader (onScanToGroup). The group is read from the live
// captureState; every change goes through the server, which decides (armed,
// this lease, Start not pressed, consent on record, the job's maximum). One
// person on the rig shows only the "Add person" button.
function ArmedGroup({ job, state, interactive, addOpen, setAddOpen, busyId = null, setBusyId = () => {}, onScanToGroup = null }) {
  const people = groupPeopleOf(state);
  const open = groupOpen(state);
  const max = maxGroupSizeOf(job);
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  // The roster, live, only while it is needed: badges for a group, or the search.
  const needRoster = !!job?.id && (people.length > 1 || addOpen);
  const { data: roster } = useFirestoreCollection(needRoster ? `jobs/${job.id}/rosterEntries` : null);
  const byId = useMemo(() => Object.fromEntries(roster.map((r) => [r.id, r])), [roster]);
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);
  // A new arm (another lease) starts with the panel closed and nothing typed.
  useEffect(() => { setQuery(""); setError(null); setAddOpen(false); }, [state.leaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (max <= 1 && people.length <= 1) return null; // groups are off for this job
  const canChange = interactive && open && !busyId;
  const atMax = people.length >= max;

  const change = async (rosterEntryId, fn, errorText = null) => {
    if (!canChange) return;
    setBusyId(rosterEntryId); setError(null);
    try {
      await fn();
      if (mountedRef.current) setQuery("");
    } catch (err) {
      if (mountedRef.current) setError(errorText ? new Error(errorText(err)) : err);
    } finally {
      if (mountedRef.current) setBusyId(null);
    }
  };
  const addEntry = (entry) => {
    const name = entry.name || entry.displayId;
    // Someone already captured joins only as a retake, asked first (or when
    // the server says so, if this roster was behind).
    change(entry.id, () => addWithRetakeCheck({
      add: (retake) => rigv2.addGroupMember({
        jobId: job.id, leaseId: state.leaseId, deviceId: getDeviceId(), rosterEntryId: entry.id, retake,
      }),
      knownCaptured: entry.captured === true,
      confirm: (question) => window.confirm(question),
      name,
    }), (err) => groupAddErrorText(err, name));
  };
  const removePerson = (p) => change(p.rosterEntryId, () => rigv2.removeGroupMember({
    jobId: job.id, leaseId: state.leaseId, deviceId: getDeviceId(), rosterEntryId: p.rosterEntryId,
  }));

  const q = query.trim().toLowerCase();
  const matches = addOpen && q
    ? roster.filter((p) => [p.displayId, p.name, p.personFields?.email]
      .filter(Boolean).map((v) => String(v).toLowerCase()).join(" ").includes(q)).slice(0, 6)
    : [];
  // A minor in the group: their guardian must be there too.
  const minors = people.filter((p) => !p.lead).map((p) => byId[p.rosterEntryId]).filter((e) => e?.personFields?.isMinor);

  return (
    <div className="rigv2-group-box">
      {people.length > 1 && <div className="rigv2-group-count">{groupCountLabel(people.length)}</div>}
      {people.length > 1 && (
        <GroupList people={people} onRemove={open ? removePerson : null} removingId={busyId} disabled={!canChange}
          badgeFor={(p) => (p.lead ? null : <RosterBadges entry={byId[p.rosterEntryId]} className="rigv2-roster-badges" />)} />
      )}
      {minors.map((e) => (
        <p key={e.id} className="rigv2-group-note warn">
          {e.name || e.displayId} is under 18: check that {e.personFields.guardianName || "their guardian"} is at the station before Start.
        </p>
      ))}
      {state.groupLocked && <p className="rigv2-group-note">Start was pressed, so the group is locked. Cancel to change it.</p>}
      {open && !addOpen && (
        <div className="rigv2-group-tools">
          <button type="button" className="rigv2-btn" disabled={!interactive || atMax || !!busyId}
            onClick={() => { setAddOpen(true); setError(null); }}>+ Add person</button>
        </div>
      )}
      {open && atMax && people.length > 1 && <p className="rigv2-group-note">{max} people is the most for one capture on this job.</p>}
      {open && addOpen && (
        <div className="rigv2-group-add">
          <input type="text" className="rigv2-field" value={query} autoFocus aria-label="Search the roster to add a person"
            onChange={(e) => setQuery(e.target.value)} placeholder="Search name or number" />
          {matches.length > 0 && (
            <div className="rigv2-matchlist">
              {matches.map((entry) => {
                const problem = groupJoinProblem(entry, state);
                return (
                  <div key={entry.id}>
                    <RosterRow entry={entry} disabled={!canChange || atMax || !!problem} onPick={() => addEntry(entry)} />
                    {problem && <span className="rigv2-group-add-why">{groupJoinProblemText(problem, entry.name || entry.displayId)}</span>}
                  </div>
                );
              })}
            </div>
          )}
          {q && matches.length === 0 && <p className="rigv2-console-hint">Nobody on the roster matches.</p>}
          <div className="rigv2-group-tools">
            {onScanToGroup && (
              <button type="button" className="rigv2-btn" disabled={!interactive || atMax}
                onClick={() => onScanToGroup({ jobId: job.id, leaseId: state.leaseId })}>Scan a pass</button>
            )}
            <button type="button" className="rigv2-btn ghost" onClick={() => { setAddOpen(false); setQuery(""); setError(null); }}>Done</button>
          </div>
          <p className="rigv2-console-hint center">One capture for everyone in the group. Each person needs their own registration with consent, and each of them gets the capture.</p>
        </div>
      )}
      {error && <div className="rigv2-banner error">{error.message ?? String(error)}</div>}
    </div>
  );
}

// Countdown and capturing: who else is in the photograph (nothing for one person).
function GroupWith({ state }) {
  const others = groupPeopleOf(state).filter((p) => !p.lead).map((p) => p.name || p.displayId);
  if (others.length === 0) return null;
  return <div className="rigv2-group-with">with {joinNames(others)} · {groupCountLabel(others.length + 1)}</div>;
}

// ── Countdown (auto-confirm — exact live wiring from RigCountdown.jsx) ─────────
// A countdown whose confirm failed (the SN could not be minted, say): Try
// again sends the confirm once more, Cancel takes the arm off the rig (the
// server cancels a countdown on its lease, and clears a group lock with it).
// A new arm never replaces a countdown, so without these the rig had no way
// out from the screens. Shown while an error or one of these calls is out.
// Pure, exported for the test.
function CountdownRecovery({ error = null, busy = null, interactive = true, onRetry, onCancel }) {
  if (!interactive || (!error && !busy)) return null;
  return (
    <div className="rigv2-actions" style={{ marginTop: 14 }}>
      <button type="button" className="rigv2-btn primary" disabled={!!busy} onClick={onRetry}>
        {busy === "retry" ? "Trying again…" : "Try again"}
      </button>
      <button type="button" className="rigv2-btn ghost" disabled={!!busy} onClick={onCancel}>
        {busy === "cancel" ? "Cancelling…" : "Cancel"}
      </button>
    </div>
  );
}

// One confirm for a countdown, the timer's or Try again's: at most one per
// job and lease in this tab (beginCountdownConfirm). A failure releases that
// guard and is reported through onError whenever the screen is still up on
// the same lease (isCurrent), never dropped because the screen re-rendered
// in between: the confirm writes the group lock itself, which changes the
// live state under the screen before the SN is even minted (Codex round 2).
// A call that finds a confirm for the same job and lease already out (the
// screen was unmounted and mounted again while it was out, a tab switch, and
// its new timer fired) joins that confirm instead of sending another, so the
// result reaches the screen that is up now (Codex round 3). Returns "ok",
// "failed", "joined" + the outcome ("joined-ok", "joined-failed"), or
// "skipped" (this lease was already confirmed from this tab). Pure (the
// calls come in), exported for the test.
async function runCountdownConfirm({ jobId, leaseId, confirm, isCurrent, onError, onStart = () => {}, onSettled = () => {} }) {
  const key = countdownConfirmKey(jobId, leaseId);
  if (!beginCountdownConfirm(key)) {
    const running = countdownConfirmRuns.get(key);
    if (!running) return "skipped";
    try {
      await running;
      return "joined-ok";
    } catch (err) {
      if (isCurrent()) onError(err);
      return "joined-failed";
    }
  }
  onStart();
  const run = Promise.resolve().then(confirm);
  countdownConfirmRuns.set(key, run);
  try {
    await run;
    return "ok";
  } catch (err) {
    releaseCountdownConfirm(key);
    if (isCurrent()) onError(err);
    return "failed";
  } finally {
    if (countdownConfirmRuns.get(key) === run) countdownConfirmRuns.delete(key);
    onSettled();
  }
}

function CountdownScreen({ job, state, interactive }) {
  const [remaining, setRemaining] = useState(3);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // "retry" | "cancel" | null
  const fireAt = useMemo(() => countdownFireAt(state.expiresAt), [state.expiresAt]);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  // The lease on the screen now, read when a confirm settles.
  const leaseRef = useRef(state.leaseId);
  leaseRef.current = state.leaseId;

  // The confirm for this screen's job and a lease; manual: Try again (shows
  // its own busy label). Its result lands whatever re-rendered the screen.
  const confirmFor = (leaseId, manual) => runCountdownConfirm({
    jobId: job.id,
    leaseId,
    confirm: () => ingest.confirm({ jobId: job.id, leaseId, deviceId: getDeviceId() }),
    isCurrent: () => mountedRef.current && leaseRef.current === leaseId,
    onError: setError,
    onStart: () => { if (manual) { setBusy("retry"); setError(null); } },
    onSettled: () => { if (manual && mountedRef.current) setBusy(null); },
  });

  const tryAgain = () => {
    if (!interactive || busy || !job?.id) return;
    confirmFor(state.leaseId, true);
  };
  const cancelCountdown = async () => {
    if (!interactive || busy || !job?.id) return;
    setBusy("cancel"); setError(null);
    try {
      await ingest.cancel({ jobId: job.id, leaseId: state.leaseId, deviceId: getDeviceId() });
    } catch (err) {
      if (mountedRef.current) setError(err);
    } finally {
      if (mountedRef.current) setBusy(null);
    }
  };

  // The timer: the number on screen, and the confirm when it reaches zero.
  // Only the REAL countdown fires the confirm callable; the preview just ticks.
  // Its cleanup stops the timer only, never the confirm's result (above), and
  // it does not restart for a new state version on the same lease (the
  // confirm's own lock write), only for a new lease or fire time.
  useEffect(() => {
    let stopped = false;
    const tick = () => { if (!stopped) setRemaining(Math.max(0, Math.ceil((fireAt - Date.now()) / 1000))); };
    tick();
    const interval = window.setInterval(tick, 250);
    const timeout = interactive && job?.id ?
      window.setTimeout(() => { confirmFor(state.leaseId, false); }, Math.max(0, fireAt - Date.now())) :
      null;
    return () => { stopped = true; window.clearInterval(interval); if (timeout) window.clearTimeout(timeout); };
  }, [fireAt, interactive, job?.id, state.leaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rigv2-stagebox">
      <div className="rigv2-count-label">Strike a pose</div>
      <div className="rigv2-subject-name" style={{ fontSize: 26 }}>{state.subjectName ?? "—"}</div>
      <GroupWith state={state} />
      <div className="rigv2-count">{remaining}</div>
      {error && <div className="rigv2-banner error" role="alert">{error.message ?? String(error)}</div>}
      <CountdownRecovery error={error} busy={busy} interactive={interactive} onRetry={tryAgain} onCancel={cancelCountdown} />
    </div>
  );
}

// ── Capturing (firing → processing → slow; reconcile from RigCapturing.jsx) ────
// The Capturing screen's Mark complete / Abandon: the call, then what the
// screen shows when it settles. Busy always clears when the call returns,
// success or failure, so the buttons come back whenever the rig is still on
// this capture (a release that did not go through is reported by the server
// with what to press). Nothing is set once the screen has gone (the rig moved
// on). The setters come in, so the test drives it without a DOM.
async function runReconcile({ call, setBusy, setError, setNote, isMounted }) {
  setBusy(true); setError(null); setNote(null);
  try {
    const res = await call();
    if (isMounted()) setNote(reconcileResultText(res));
  } catch (e) {
    if (isMounted()) setError(e?.message ?? String(e));
  } finally {
    if (isMounted()) setBusy(false);
  }
}

// The line after a Mark complete or Abandon that went through while this
// screen is still up (usually it has already gone back to Next capture).
function reconcileResultText(res) {
  if (res?.rig === "released") return "Done. The rig is free; the screen changes in a moment.";
  if (res?.rig === "moved_on") return "Done. The rig had already moved on.";
  return "Done.";
}

function CapturingScreen({ job, state, interactive, forceElapsed }) {
  const [elapsed, setElapsed] = useState(forceElapsed ?? 0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const start = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (forceElapsed != null) return undefined;
    start.current = Date.now();
    const t = setInterval(() => setElapsed(Date.now() - start.current), 250);
    return () => clearInterval(t);
  }, [state.captureAttemptId, forceElapsed]);

  const stage = capturingStage(elapsed);
  const slow = stage === "slow";
  const processing = stage === "processing";
  const showActions = stage !== "firing";

  const reconcile = async (decision) => {
    if (!interactive || busy || !state.captureAttemptId) return;
    if (!window.confirm(decision === "abandon" ? ABANDON_CONFIRM : MARK_COMPLETE_CONFIRM)) return;
    await runReconcile({
      call: () => ingest.reconcile({ jobId: job.id, captureAttemptId: state.captureAttemptId, decision, note: `operator-${decision} via rig v2` }),
      setBusy, setError, setNote, isMounted: () => mountedRef.current,
    });
  };

  return (
    <div className="rigv2-stagebox">
      <div className={`rigv2-eyebrow ${slow ? "danger" : "capture"}`}>
        <span className="rigv2-dot" />{slow ? "Slow capture" : processing ? "Downloading from cameras" : "Firing"}
      </div>
      <div className="rigv2-avatar firing">{initials(state.subjectName)}</div>
      <div className="rigv2-subject-name">{state.subjectName || "—"}</div>
      <GroupWith state={state} />
      <div className="rigv2-subject-id">SN {state.captureAttemptId || "—"}</div>
      <div className="rigv2-hold">{slow ? "Taking longer than usual — check xangle" : processing ? "Pulling files…" : "Hold still"}</div>
      {slow && <div className="rigv2-banner warn">Capture has run past {CAPTURE_SLOW_MS / 1000}s. Mark it partial or abandon if xangle is stuck.</div>}
      {showActions && (
        <div className="rigv2-actions" style={{ marginTop: 14 }}>
          <button type="button" className="rigv2-btn primary" disabled={busy || !interactive} onClick={() => reconcile("complete")}>
            {busy ? "Working…" : "Mark complete"}
          </button>
          <button type="button" className="rigv2-btn ghost" disabled={busy || !interactive} onClick={() => reconcile("abandon")}>Abandon · burn SN</button>
        </div>
      )}
      {note && !error && <p className="rigv2-console-hint center" role="status">{note}</p>}
      {error && <div className="rigv2-banner error" role="alert">{error}</div>}
    </div>
  );
}

// ── Bridge-fed visual states (Segment 4 feed; preview-only under live data) ────
function MintFailedScreen({ state }) {
  return (
    <div className="rigv2-stagebox">
      <div className="rigv2-eyebrow danger"><span className="rigv2-dot" />Held · waiting for a serial number</div>
      <div className="rigv2-avatar">{initials(state.subjectName)}</div>
      <div className="rigv2-subject-name">{state.subjectName || "—"}</div>
      <div className="rigv2-hold">The SN allocator is unreachable. The capture is held until it comes back.</div>
      <div className="rigv2-actions"><button type="button" className="rigv2-btn">Retry now</button><button type="button" className="rigv2-btn ghost">Abandon</button></div>
      <div className="rigv2-banner warn">Needs the rig-bridge mint feed (Segment 4) to drive automatically.</div>
    </div>
  );
}
function PartialScreen({ state }) {
  return (
    <div className="rigv2-stagebox">
      <div className="rigv2-eyebrow capture"><span className="rigv2-dot" />Captured · flagged partial</div>
      <div className="rigv2-avatar">{initials(state.subjectName)}</div>
      <div className="rigv2-subject-name">{state.subjectName || "—"}</div>
      <div className="rigv2-hold">Landed with fewer cameras than expected (e.g. 168 / 175).</div>
      <div className="rigv2-banner warn">Needs the bridge camera-count feed (Segment 4) to flag automatically.</div>
    </div>
  );
}
function HeldScreen() {
  return (
    <div className="rigv2-stagebox">
      <div className="rigv2-eyebrow muted"><span className="rigv2-dot" />Held entries · transfer stuck</div>
      <div className="rigv2-hold">Some captures staged but failed to copy to the destination. Non-blocking; auto-retrying.</div>
      <div className="rigv2-banner warn">Needs the bridge journal feed (Segment 4) to populate.</div>
    </div>
  );
}

export {
  RIGS,
  MODE_WIRED,
  StateScreen,
  IdleScreen,
  RegisterFields,
  RosterRow,
  RosterBadges,
  RegQrOverlay,
  registerReady,
  consentTick,
  consentTickStale,
  wordingChangedSince,
  dropStaleTicks,
  mergeSavedEmail,
  RecentCapturesStrip,
  PassEmailRow,
  maskEmail,
  isTypingTarget,
  armedShortcutFires,
  passEmailResultText,
  ArmedScreen,
  ArmedGroup,
  GroupWith,
  CountdownScreen,
  CapturingScreen,
  beginCountdownConfirm,
  countdownConfirmKey,
  countdownFireAt,
  releaseCountdownConfirm,
  resetCountdownConfirmGuardsForTests,
  CAPTURE_FIRING_HOLD_MS,
  CAPTURE_SLOW_MS,
  ABANDON_CONFIRM,
  MARK_COMPLETE_CONFIRM,
  CountdownRecovery,
  runCountdownConfirm,
  capturingStage,
  callErrorCode,
  isClearRefusal,
  retakeQuestion,
  isRetakeRefusal,
  addWithRetakeCheck,
  groupAddErrorText,
  registerThenArmFlow,
  runReconcile,
  reconcileResultText,
};
